import React, { useState, useEffect } from 'react';
import { ClipboardList, Plus, Check, Trash2, Clock, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';

const ShiftTasks = () => {
  const { currentUser } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('all'); // all, pending, completed
  const [newTask, setNewTask] = useState({
    text: '',
    priority: 'normal', // low, normal, high, urgent
    category: 'general'
  });

  const categories = [
    { id: 'general', label: 'General', emoji: '📋' },
    { id: 'calls', label: 'Phone Calls', emoji: '📞' },
    { id: 'refills', label: 'Refills', emoji: '💊' },
    { id: 'followup', label: 'Follow-ups', emoji: '🔄' },
    { id: 'restock', label: 'Restock', emoji: '📦' },
    { id: 'cleaning', label: 'Cleaning', emoji: '🧹' },
    { id: 'handoff', label: 'Handoff Notes', emoji: '📝' },
  ];

  const priorities = [
    { id: 'low', label: 'Low', color: 'bg-gray-100 text-gray-600 border-gray-200' },
    { id: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-600 border-blue-200' },
    { id: 'high', label: 'High', color: 'bg-orange-100 text-orange-600 border-orange-200' },
    { id: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-600 border-red-200' },
  ];

  // Load tasks from localStorage
  useEffect(() => {
    if (currentUser) {
      const savedTasks = localStorage.getItem(`shiftTasks_${currentUser.uid}`);
      if (savedTasks) {
        setTasks(JSON.parse(savedTasks));
      }
    }
  }, [currentUser]);

  // Save tasks to localStorage
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem(`shiftTasks_${currentUser.uid}`, JSON.stringify(tasks));
    }
  }, [tasks, currentUser]);

  const addTask = () => {
    if (!newTask.text.trim()) return;

    const task = {
      id: Date.now(),
      text: newTask.text.trim(),
      priority: newTask.priority,
      category: newTask.category,
      completed: false,
      createdAt: new Date().toISOString(),
      completedAt: null
    };

    setTasks(prev => [task, ...prev]);
    setNewTask({ text: '', priority: 'normal', category: 'general' });
    setShowAddModal(false);
  };

  const toggleTask = (id) => {
    setTasks(prev => prev.map(task => {
      if (task.id === id) {
        return {
          ...task,
          completed: !task.completed,
          completedAt: !task.completed ? new Date().toISOString() : null
        };
      }
      return task;
    }));
  };

  const deleteTask = (id) => {
    setTasks(prev => prev.filter(task => task.id !== id));
  };

  const clearCompleted = () => {
    if (window.confirm('Clear all completed tasks?')) {
      setTasks(prev => prev.filter(task => !task.completed));
    }
  };

  const clearAll = () => {
    if (window.confirm('Clear ALL tasks? This cannot be undone.')) {
      setTasks([]);
    }
  };

  const getFilteredTasks = () => {
    let filtered = [...tasks];
    
    if (filter === 'pending') {
      filtered = filtered.filter(t => !t.completed);
    } else if (filter === 'completed') {
      filtered = filtered.filter(t => t.completed);
    }

    // Sort: urgent first, then by creation time
    filtered.sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      const priorityOrder = { urgent: 0, high: 1, normal: 2, low: 3 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return filtered;
  };

  const getCategoryEmoji = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.emoji : '📋';
  };

  const getPriorityStyle = (priorityId) => {
    const priority = priorities.find(p => p.id === priorityId);
    return priority ? priority.color : priorities[1].color;
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const pendingCount = tasks.filter(t => !t.completed).length;
  const completedCount = tasks.filter(t => t.completed).length;
  const urgentCount = tasks.filter(t => !t.completed && t.priority === 'urgent').length;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-brand-brown flex items-center">
            <ClipboardList className="w-8 h-8 mr-3 text-pastel-green" />
            Shift Tasks
          </h1>
          <p className="text-brand-brown/60 mt-1">Track your tasks for today's shift</p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="card p-3 text-center">
            <p className="text-2xl font-bold text-pastel-purple">{pendingCount}</p>
            <p className="text-xs text-brand-brown/60">Pending</p>
          </div>
          <div className="card p-3 text-center">
            <p className="text-2xl font-bold text-pastel-green">{completedCount}</p>
            <p className="text-xs text-brand-brown/60">Completed</p>
          </div>
          <div className={`card p-3 text-center ${urgentCount > 0 ? 'bg-red-50 border-red-200' : ''}`}>
            <p className={`text-2xl font-bold ${urgentCount > 0 ? 'text-red-500' : 'text-brand-brown/40'}`}>
              {urgentCount}
            </p>
            <p className="text-xs text-brand-brown/60">Urgent</p>
          </div>
        </div>

        {/* Filter Tabs & Add Button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex space-x-2">
            {[
              { id: 'all', label: 'All' },
              { id: 'pending', label: 'Pending' },
              { id: 'completed', label: 'Done' },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === f.id
                    ? 'bg-pastel-purple text-white'
                    : 'bg-white text-brand-brown/70 hover:bg-pastel-purple/10'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary flex items-center px-4 py-2"
          >
            <Plus className="w-5 h-5 mr-1" />
            Add Task
          </button>
        </div>

        {/* Tasks List */}
        <div className="space-y-2">
          {getFilteredTasks().length === 0 ? (
            <div className="card p-8 text-center">
              <ClipboardList className="w-12 h-12 mx-auto text-brand-brown/20 mb-3" />
              <p className="text-brand-brown/60">
                {filter === 'completed' ? 'No completed tasks yet' : 
                 filter === 'pending' ? 'All tasks completed! 🎉' : 
                 'No tasks yet. Add one to get started!'}
              </p>
            </div>
          ) : (
            getFilteredTasks().map(task => (
              <div
                key={task.id}
                className={`card p-3 flex items-center space-x-3 transition-all ${
                  task.completed ? 'opacity-60 bg-gray-50' : ''
                } ${task.priority === 'urgent' && !task.completed ? 'border-2 border-red-300 bg-red-50' : ''}`}
              >
                {/* Checkbox */}
                <button
                  onClick={() => toggleTask(task.id)}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    task.completed
                      ? 'bg-pastel-green border-pastel-green text-white'
                      : 'border-brand-brown/30 hover:border-pastel-purple'
                  }`}
                >
                  {task.completed && <Check className="w-4 h-4" />}
                </button>

                {/* Task Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{getCategoryEmoji(task.category)}</span>
                    <p className={`text-brand-brown ${task.completed ? 'line-through' : ''}`}>
                      {task.text}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityStyle(task.priority)}`}>
                      {task.priority}
                    </span>
                    <span className="text-xs text-brand-brown/40 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatTime(task.createdAt)}
                    </span>
                  </div>
                </div>

                {/* Delete Button */}
                <button
                  onClick={() => deleteTask(task.id)}
                  className="p-2 text-brand-brown/40 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Clear Buttons */}
        {tasks.length > 0 && (
          <div className="flex justify-center space-x-3 mt-6">
            {completedCount > 0 && (
              <button
                onClick={clearCompleted}
                className="text-sm text-brand-brown/60 hover:text-pastel-purple"
              >
                Clear completed
              </button>
            )}
            <button
              onClick={clearAll}
              className="text-sm text-brand-brown/60 hover:text-red-500"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Add Task Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-brand-brown">Add Task</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Task Input */}
              <input
                type="text"
                value={newTask.text}
                onChange={(e) => setNewTask(prev => ({ ...prev, text: e.target.value }))}
                placeholder="What needs to be done?"
                className="input w-full mb-4"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && addTask()}
              />

              {/* Category Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-brand-brown mb-2">Category</label>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setNewTask(prev => ({ ...prev, category: cat.id }))}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                        newTask.category === cat.id
                          ? 'bg-pastel-purple text-white'
                          : 'bg-gray-100 text-brand-brown hover:bg-gray-200'
                      }`}
                    >
                      {cat.emoji} {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Priority Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-brand-brown mb-2">Priority</label>
                <div className="flex space-x-2">
                  {priorities.map(p => (
                    <button
                      key={p.id}
                      onClick={() => setNewTask(prev => ({ ...prev, priority: p.id }))}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-colors ${
                        newTask.priority === p.id
                          ? p.color + ' ring-2 ring-offset-1 ring-brand-brown/20'
                          : 'bg-white border-gray-200 text-brand-brown/60'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Add Button */}
              <button
                onClick={addTask}
                disabled={!newTask.text.trim()}
                className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Task
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ShiftTasks;
